import type { ResourceLibraryItemBase } from './resourceLibraryItems';
import type { Asset } from './Asset';

/** Allowed Pimcore template identifiers for site page resources. */
export const RESOURCE_LIBRARY_SITE_PAGE_TEMPLATES = ['ApplicationArticle', 'Generic'] as const;

/** Pimcore template code for a site page resource. */
export type ResourceLibrarySitePageTemplate = (typeof RESOURCE_LIBRARY_SITE_PAGE_TEMPLATES)[number];

interface ResourceLibrarySitePageLinkBase {
    /** Original value selected in Pimcore link field (may be relative or absolute). */
    rawPath: string;
    /** Hash fragment pointing to a section within the linked page. */
    anchor: string | null;
    openInNewTab: boolean;
    /** Custom query parameters stored alongside the link. */
    parameters: string | null;
}

export interface ResourceLibrarySitePageExternalLink extends ResourceLibrarySitePageLinkBase {
    kind: 'external';
    /** Fully qualified URL to open. */
    url: string;
}

export interface ResourceLibrarySitePageInternalLink extends ResourceLibrarySitePageLinkBase {
    kind: 'internal';
    /** App-relative route starting with `/`. */
    route: string;
}

export type ResourceLibrarySitePageLink =
    | ResourceLibrarySitePageExternalLink
    | ResourceLibrarySitePageInternalLink;

// Section blocks

export type ResourceLibrarySitePageSectionKind =
    | 'headingSection'
    | 'plainTextSection'
    | 'richTextSection'
    | 'plainTextImageSection'
    | 'richTextImageSection';

type EnsureSectionKind<T extends ResourceLibrarySitePageSectionKind> = T;

export interface ResourceLibrarySitePageSectionBase {
    kind: ResourceLibrarySitePageSectionKind;
    level: number;
    mergeWithPrevious: boolean;
    title: string | null;
}

export interface ResourceLibrarySitePageHeadingSection extends ResourceLibrarySitePageSectionBase {
    kind: EnsureSectionKind<'headingSection'>;
    title: string;
}

export interface ResourceLibrarySitePagePlainTextSection
    extends ResourceLibrarySitePageSectionBase {
    kind: EnsureSectionKind<'plainTextSection'>;
    content: string;
    /** Plaintext content split into an array of paragraphs. */
    contentParagraphs: string[];
}

export interface ResourceLibrarySitePageRichTextSection extends ResourceLibrarySitePageSectionBase {
    kind: EnsureSectionKind<'richTextSection'>;
    content: string;
    /** Sanitized HTML content ready for rendering. */
    sanitizedContent: string;
}

interface ResourceLibrarySitePageImageSectionBase extends ResourceLibrarySitePageSectionBase {
    imageUrl: string;
    imageAltText: string | null;
    imageTitle: string | null;
    imageCaption: string | null;
    /** Optional link assigned to the image thumbnail; not used yet. */
    imageLink: ResourceLibrarySitePageLink | null;
}

export interface ResourceLibrarySitePagePlainTextImageSection
    extends ResourceLibrarySitePageImageSectionBase {
    kind: EnsureSectionKind<'plainTextImageSection'>;
    content: string;
    /** Plaintext content split into an array of paragraphs. */
    contentParagraphs: string[];
}

export interface ResourceLibrarySitePageRichTextImageSection
    extends ResourceLibrarySitePageImageSectionBase {
    kind: EnsureSectionKind<'richTextImageSection'>;
    content: string;
    /** Sanitized HTML content ready for rendering. */
    sanitizedContent: string;
}

export type ResourceLibrarySitePageTextImageSection =
    | ResourceLibrarySitePagePlainTextImageSection
    | ResourceLibrarySitePageRichTextImageSection;

export type ResourceLibrarySitePageSectionBlockKind = ResourceLibrarySitePageSectionKind;
export type ResourceLibrarySitePageSectionBlockBase = ResourceLibrarySitePageSectionBase;

export type ResourceLibrarySitePageSectionBlock =
    | ResourceLibrarySitePageHeadingSection
    | ResourceLibrarySitePagePlainTextSection
    | ResourceLibrarySitePageRichTextSection
    | ResourceLibrarySitePagePlainTextImageSection
    | ResourceLibrarySitePageRichTextImageSection;

export function isSitePageSectionBlock(
    section: ResourceLibrarySitePageSection
): section is ResourceLibrarySitePageSectionBlock {
    return (
        section.kind === 'headingSection' ||
        section.kind === 'plainTextSection' ||
        section.kind === 'richTextSection' ||
        section.kind === 'plainTextImageSection' ||
        section.kind === 'richTextImageSection'
    );
}

// Content blocks

export type ResourceLibrarySitePageBlockKind =
    | 'imageBlock'
    | 'videoBlock'
    | 'internalLinkBlock'
    | 'tableBlock'
    | 'richTableBlock';

type EnsureContentBlockKind<T extends ResourceLibrarySitePageBlockKind> = T;

export interface ResourceLibrarySitePageBlockSectionBase {
    kind: ResourceLibrarySitePageBlockKind;
}

export interface VideoInternal {
    kind: 'internal';
    asset: Asset;
}

export interface VideoExternal {
    kind: 'external';
    /** Original pasted value from CMS (ID or URL). */
    original: string;
    /** Normalized embed URL suitable for iframe src attribute. */
    embedUrl: string;
    /** Optional metadata captured during normalization. */
    provider: 'youtube' | 'vimeo' | 'dailymotion' | 'unknown';
    videoId: string;
}

export type NormalizedVideo = VideoInternal | VideoExternal;

export interface ResourceLibrarySitePageVideoBlock extends ResourceLibrarySitePageBlockSectionBase {
    kind: EnsureContentBlockKind<'videoBlock'>;
    video: NormalizedVideo;
    ariaLabel: string;
}

export interface ResourceLibrarySitePageImageBlock extends ResourceLibrarySitePageBlockSectionBase {
    kind: EnsureContentBlockKind<'imageBlock'>;
    imageUrl: string;
    imageAltText: string | null;
    imageTitle: string | null;
    imageCaption: string | null;
    thumbnailUrl: string;
    /** Optional link assigned to the image thumbnail; not used yet. */
    imageLink: ResourceLibrarySitePageLink | null;
}

export interface ResourceLibrarySitePageInternalLinkBlock
    extends ResourceLibrarySitePageBlockSectionBase {
    kind: EnsureContentBlockKind<'internalLinkBlock'>;
    openInNewTab: boolean;
    linkText: string;
    linkRoute: string;
}

export interface ResourceLibrarySitePageTableBlock extends ResourceLibrarySitePageBlockSectionBase {
    kind: EnsureContentBlockKind<'tableBlock'>;
    firstRowIsHeader: boolean;
    firstColumnIsHeader: boolean;
    rows: string[][];
    caption: string | null;
}

export interface ResourceLibrarySitePageRichTableBlock
    extends ResourceLibrarySitePageBlockSectionBase {
    kind: EnsureContentBlockKind<'richTableBlock'>;
    tableHtml: string;
    caption: string | null;
}

export type ResourceLibrarySitePageContentBlock =
    | ResourceLibrarySitePageImageBlock
    | ResourceLibrarySitePageVideoBlock
    | ResourceLibrarySitePageInternalLinkBlock
    | ResourceLibrarySitePageTableBlock
    | ResourceLibrarySitePageRichTableBlock;

export type ResourceLibrarySitePageContentBlockKind = ResourceLibrarySitePageBlockKind;
export type ResourceLibrarySitePageContentBlockBase = ResourceLibrarySitePageBlockSectionBase;

export function isInternalLinkBlock(
    section: ResourceLibrarySitePageSection
): section is ResourceLibrarySitePageInternalLinkBlock {
    return section.kind === 'internalLinkBlock';
}

export function isSitePageContentBlock(
    section: ResourceLibrarySitePageSection
): section is ResourceLibrarySitePageContentBlock {
    return (
        section.kind === 'imageBlock' ||
        section.kind === 'videoBlock' ||
        section.kind === 'internalLinkBlock' ||
        section.kind === 'tableBlock' ||
        section.kind === 'richTableBlock'
    );
}

// SitePage items

export type ResourceLibrarySitePageSection =
    | ResourceLibrarySitePageSectionBlock
    | ResourceLibrarySitePageContentBlock;

export type ResourceLibrarySitePageBlock = ResourceLibrarySitePageSection;

type EnsureTemplate<T extends ResourceLibrarySitePageTemplate> = T;

export interface ResourceLibrarySitePageBaseItem extends ResourceLibraryItemBase {
    kind: 'sitePage';
    template: ResourceLibrarySitePageTemplate;
    coverImageUrl: string | null; // becouse we use SitePage items as news items
}

export interface ResourceLibrarySitePageItemCommonFields extends ResourceLibrarySitePageBaseItem {
    navigationNodeId: string | null;
    sections: ResourceLibrarySitePageBlock[];
}

export interface ResourceLibrarySitePageItem extends ResourceLibrarySitePageItemCommonFields {}

export interface ResourceLibrarySitePageApplicationArticleItem extends ResourceLibrarySitePageItem {
    template: EnsureTemplate<'ApplicationArticle'>;
    sections: ResourceLibrarySitePageBlock[];
}

export interface ResourceLibrarySitePageGenericItem extends ResourceLibrarySitePageItem {
    template: EnsureTemplate<'Generic'>;
    sections: ResourceLibrarySitePageBlock[];
}

export function isSitePageApplicationArticleItem(
    item: ResourceLibrarySitePageItem
): item is ResourceLibrarySitePageApplicationArticleItem {
    return item.template === 'ApplicationArticle';
}

export function isSitePageGenericItem(
    item: ResourceLibrarySitePageItem
): item is ResourceLibrarySitePageGenericItem {
    return item.template === 'Generic';
}
